import _ from 'lodash';
import { useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import { TEAM_ROLES, VIEWER_ROLE } from '../../constants/teamRoles';
import { asQuantityWithUnit } from '../../utils/formatters';
import EmailForm from '../EmailForm';
import ArrowIcon from '../icons/ArrowIcon';

const isEditor = ({ role }) => role === 'editor' || role === 'admin';

const DoneButton = styled(({ showSkip, onClick, className }) => (
  <Button
    color="link"
    onClick={onClick}
    className={`text-primary p-0 font-large ${className}`}
  >
    {showSkip ? 'Skip' : 'Done'}
    <ArrowIcon direction="right" className="ml-2" />
  </Button>
))`
  line-height: 1.2;
  svg {
    height: 18px;
    width: 18px;
    margin-bottom: 3px;
  }
`;

const MembersContent = ({ onCompleted, onEditPlan, seats }) => {
  const [invitedMembers, setInvitedMembers] = useState({});
  const nInvitedEditors = useMemo(
    () => Object.values(invitedMembers).filter(isEditor).length,
    [invitedMembers],
  );
  const nInvitedMembers = useMemo(() => Object.values(invitedMembers).length, [
    invitedMembers,
  ]);
  const seatsLeft = seats - nInvitedEditors;
  const availableRoles = useMemo(
    () => (seatsLeft > 0 ? TEAM_ROLES : VIEWER_ROLE),
    [seatsLeft],
  );

  return (
    <div>
      <div className="d-flex justify-content-between mb-4">
        <h1 className="m-0">Invite team members</h1>
        {seatsLeft >= 0 && (
          <DoneButton
            showSkip={nInvitedMembers === 0}
            onClick={() => {
              onCompleted({ members: Object.values(invitedMembers) });
            }}
          />
        )}
      </div>
      <div className="mb-3">
        <h2 className="mb-1">
          Invite:{' '}
          {seatsLeft > 0
            ? `${seatsLeft} of ${seats} editors remaining`
            : 'No remaining editors'}
        </h2>
        <Button color="link" className="p-0 font-tiny" onClick={onEditPlan}>
          Add editors
        </Button>
      </div>
      {seatsLeft < 0 && (
        <span className="text-danger">
          Please remove{' '}
          {asQuantityWithUnit('editors', 'editor')(Math.abs(seatsLeft))} or
          upgrade your plan.
        </span>
      )}
      <div className="mb-4">
        <EmailForm
          roles={availableRoles}
          onSubmit={({ emails }, { resetForm }) => {
            setInvitedMembers({
              ...invitedMembers,
              ..._.keyBy(emails, 'email'),
            });
            resetForm();
          }}
          submitButtonText="Add members"
        />
      </div>
      <div className="mb-4">
        {Object.values(invitedMembers).map((member) => (
          <div
            key={member.email}
            className="d-flex justify-content-between mb-1"
          >
            <div>{member.email}</div>
            <div>
              <span className="mr-4">{_.capitalize(member.role)}</span>
              <Button
                onClick={() =>
                  setInvitedMembers(_.omit(invitedMembers, member.email))
                }
                color="link"
                size="sm"
                className="p-0"
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MembersContent;
